import React, { useContext, useState } from 'react'
import useGetAlertsReportData from './hooks/useGetAlertsReportData';
import { Button, Checkbox, Loading, Menu, MenuItem, Modal, Popover, TextInput } from '@sede-enterprise/shell-ds-react-framework';
import DataTable from '../../components/DataTable/DataTable';
import { Link, useHistory, useParams } from 'react-router-dom/cjs/react-router-dom';
import { UnitContext } from '../../App';
import { alertStatusOptions } from '../../util/alertStatusOptions';
import { toast } from 'react-toastify';
import { mmOrInch, mmOrMiles, splitToArray } from '../../util/helperFunctions';
import { addComments, getCheckboxHeaderState, handleAllRowsSelection, handleCheckboxSelection, isRowSelected, resolveAlerts } from './util/alertsUtil';
import { DE_INSTALL_HELP, DE_INSTALLED_PROD_UNIT } from '../../util/helperTexts';

import * as R from "rambda";



const pageSize = 10;
const INIT_PAGE = 1;
const INIT_ORDER = "alertStartDate";
const INIT_ORDER_DIRECTION = 1



const AlertsReportData = () => {

    const history = useHistory();
    const { id, site, category, start: stringifiedStart, end: strinigifiedEnd, duration } = useParams();
    const start = JSON.parse(stringifiedStart);
    const end = JSON.parse(strinigifiedEnd)



    const sensorListFilter = `alerts.alertStartDate>='${start}' AND alerts.alertEndDate<='${end}' AND sensor.site = '${site}' AND sensor.excludeFromUi = 0 AND alerts.category = '${category}' AND sensor.id = '${decodeURIComponent(id)}'${duration ? ` AND alerts.duration= '${duration}'` : ''}`;

    const context = useContext(UnitContext);

    const [currentPage, setCurrentPage] = useState(INIT_PAGE);
    const [sensorOrder, setSensorOrder] = useState(INIT_ORDER);
    const [orderDirection, setOrderDirection] = useState(INIT_ORDER_DIRECTION);

    const handleOrderChange = (sortBy, direction) => {
        setSensorOrder(sortBy);
        setOrderDirection(+direction);
    }

    const handleCurrentPageChange = (current) => {
        setCurrentPage(current);
    }

    const [refreshToggle, setRefreshToggle] = useState(false);
    const [userComment, setUserComment] = useState("");
    const [openModal, setOpenModal] = useState(false);
    const handleModalClose = () => {
        setOpenModal(false)
        setUserComment("")
    }
    const [popupVisible, setPopupVisible] = useState(false);
    const handleOnPopupClick = () => {
        setPopupVisible(!popupVisible);
    };


    const order_dir = orderDirection === 1 ? `DESC` : `ASC`;
    
    const [selectedRows, setSelectedRows] = useState([]);

    const { data: corrosionAlertsData, loading } = useGetAlertsReportData(currentPage, pageSize, sensorOrder, order_dir, sensorListFilter, refreshToggle);

    const columns = [
        {
            id: 'rowSelection',
            header: () => {
                const { isChecked, isIndeterminate } = getCheckboxHeaderState(corrosionAlertsData, selectedRows);
                return <Checkbox indeterminate={isIndeterminate} checked={isChecked} onChange={(e) => handleAllRowsSelection(e, selectedRows, setSelectedRows, corrosionAlertsData)} />
            },
            cell: (props) => {
                const row = props.row.original;
                const isSelectedRow = isRowSelected(selectedRows, row);
                return <Checkbox checked={isSelectedRow} onChange={handleCheckboxSelection(row, selectedRows, setSelectedRows)} />
            }
        },
        {
            header: 'Start Date',
            accessorFn: (row) => row.alertStartDate,
            id: 'alertStartDate'
        },
        {
            header: 'End Date',
            accessorFn: (row) => row.alertEndDate,
            id: 'alertEndDate'
        },
        {
            header: 'Sensor',
            accessorFn: row => row.isRealSensor && row.name,
            id: 'sensor_id',
            cell: (props) => {
                const row = props.row.original;
                return <Link
                    className={
                        row.uninstalled
                            ? "link redContent"
                            : "link blueContent"
                    }
                    to={`/home/${encodeURIComponent(row.sensor_id)}/details`}
                    title={
                        row.uninstalled
                            ? R.path(
                                row.conditionMonitoringLocation
                            ) === DE_INSTALLED_PROD_UNIT
                                ? DE_INSTALL_HELP.bukam
                                : DE_INSTALL_HELP.other
                            : ""
                    }
                >
                    {row.isRealSensor && row.sensor_id}
                    {!row.isRealSensor && <span title="This CML has no sensor installed" className="redContent">No Sensor</span>}

                </Link>
            }
        },
        {
            header: 'CML',
            id: 'sensor.cmLocation.shortName',
            accessorFn: row => row.conditionMonitoringLocation,
            cell: (props) => {
                const row = props.row.original;
                return row.conditionMonitoringLocationName ? (
                    <Link
                        className="link"
                        to={`/cmls/${encodeURIComponent(row.conditionMonitoringLocation)}/details`}
                    >

                        {row.conditionMonitoringLocationName}
                    </Link>
                ) : null
            },
        },
        {
            header: "Average CR (" + mmOrMiles(context.units) + ")",
            id: 'averageCRValue',
            cell: (props) => {
                const row = props.row.original;
                return context.units
                    ? (39.3700787402 * R.path(["averageCRValue"], row))?.toFixed(3)
                    : R.path(["averageCRValue"], row)?.toFixed(3)
            },
            accessorFn: (row) =>
                context.units
                    ? (39.3700787402 * R.path(["averageCRValue"], row))?.toFixed(3)
                    : R.path(["averageCRValue"], row)?.toFixed(3)
        },
        {
            header: 'Category',
            id: 'category',
            accessorFn: (row) => row.category
        },
        {
            header: <p>WL<sub>meas</sub>({mmOrInch(context.units)})</p>,
            id: 'measuredWallThicknessLoss',
            cell: (props) => {
                const row = props.row.original;
                return context.units
                    ? (
                        0.0393700787 *
                        R.pathOr(0, ["measuredWallThicknessLoss"], row)
                    ).toFixed(3)
                    : R.pathOr(0, ["measuredWallThicknessLoss"], row).toFixed(3)
            },
            accessorFn: (row) =>
                context.units
                    ? (
                        0.0393700787 *
                        R.pathOr(0, ["measuredWallThicknessLoss"], row)
                    ).toFixed(3)
                    : R.pathOr(0, ["measuredWallThicknessLoss"], row).toFixed(3),
        },
        {
            header: <p>WL<sub>pred</sub>({mmOrInch(context.units)})</p>,
            id: 'predictedWallThicknessLoss',
            cell: (props) => {
                const row = props.row.original;
                return context.units
                    ? (
                        0.0393700787 *
                        R.pathOr(0, ["predictedWallThicknessLoss"], row)
                    ).toFixed(3)
                    : R.pathOr(0, ["predictedWallThicknessLoss"], row).toFixed(3)
            },
            accessorFn: (row) =>
                context.units
                    ? (
                        0.0393700787 *
                        R.pathOr(0, ["predictedWallThicknessLoss"], row)
                    ).toFixed(3)
                    : R.pathOr(0, ["predictedWallThicknessLoss"], row).toFixed(3),
        },
        {
            header: "Alert Threshold (" + mmOrMiles(context.units) + ")",
            id: "sensor.alertThreshold",
            cell: (props) => {
                const row = props.row.original;
                return context.units
                    ? (39.3700787402 * R.path(["alertThreshold"], row)).toFixed(
                        3
                    )
                    : R.path(["alertThreshold"], row)
            },
            accessorFn: (row) =>
                context.units
                    ? (39.3700787402 * R.path(["alertThreshold"], row)).toFixed(
                        3
                    )
                    : R.path(["alertThreshold"], row),
        },
        {
            header: 'Status',
            id: 'status',
            accessorFn: (row) => R.path(["status"], row)
        },
        {
            header: 'Comments',
            id: 'comments',
            accessorFn: (row) => R.path(["comments"], row)
        }
    ]
 
    // Function to trigger data refresh
    const refreshData = () => {
        setSelectedRows([])
        setRefreshToggle(prev => !prev); // Flip the value to trigger useEffect
    };

    const handleResolveAlerts = (actionKey) => {
        resolveAlerts(selectedRows, actionKey, refreshData);
    };

    const handleAddComments = () => {
        addComments(selectedRows, userComment, refreshData, handleModalClose, setUserComment);
    };



    const statusOverlay = (
        <Menu>
            {alertStatusOptions
                .filter(status => {
                    return status.resolve;
                })
                .map(status => (
                    <MenuItem key={status.value} onClick={(e) => handleResolveAlerts(e.key)}>{status.label}</MenuItem>
                ))}
        </Menu>
    )

    return (
        <div className='parent-container'>
            <div className='content-container'>
                <div className='content-header'>
                    <h2>{category} Alerts</h2>
                    <div className='header-actions'>
                        <Popover popup={statusOverlay}
                            onPopupClick={(e) => {
                                handleOnPopupClick()
                                e.stopPropagation()
                            }
                            }
                            popupVisible={popupVisible} arrow onPopupVisibleChange={setPopupVisible}
                        >
                            <Button onClick={() => { }}
                                disabled={selectedRows.length === 0}
                            >Resolve Alerts</Button>
                        </Popover>
                        <Button onClick={() => setOpenModal(true)}
                            disabled={selectedRows.length === 0}
                        >Add Comments</Button>
                    </div>
                </div>
                <div className='content-body'>
                    {corrosionAlertsData?.alertsList?.length <= 0 && loading && <div className='loading-container' role='status'><Loading /></div>}
                    {
                        corrosionAlertsData?.alertsList?.length > 0 &&
                        <DataTable data={corrosionAlertsData.alertsList}
                            columns={columns}
                            count={corrosionAlertsData.alertsCount}
                            currentPage={currentPage}
                            handleCurrentPageChange={handleCurrentPageChange}
                            handleOrderChange={handleOrderChange}
                            INIT_ORDER={INIT_ORDER}
                            INIT_ORDER_DIRECTION={INIT_ORDER_DIRECTION}
                            loading={loading}
                        />
                    }
                    <div className='header-actions'>
                        <Button onClick={() => history.push(`/home/${id}/details`)}>Back to Sensor Details</Button>
                        <Button onClick={() => history.push("/alerts")}>View all Alerts</Button>
                    </div>
                </div>
            </div>

            <Modal open={openModal} onClose={handleModalClose} title="Add Comment"
                actions={[
                    {
                        label: 'Submit',
                        action: handleAddComments,
                        props: {
                            disabled: userComment.length <= 0
                        }
                    },
                    {
                        label: 'Cancel',
                        action: () => {
                            handleModalClose();
                        },
                        props: {
                            variant: 'outlined'
                        }
                    }

                ]}
            >
                <TextInput placeholder='Add Your Comments'
                    value={userComment}
                    onChange={(e) => setUserComment(e.target.value)}
                />
            </Modal>
        </div>
    )
}

export default AlertsReportData